import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileBarChart, Gauge, Zap } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { REPORT_STATUS, STATEMENT_NATURE, fmtDate, fmtDateTime } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";
import { ActionButtons, useProjectPicker } from "../../components/ops";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TABS = [
  { id: "periodos", label: "Periodos", icon: FileBarChart },
  { id: "calidad_dato", label: "Calidad del dato", icon: Gauge },
  { id: "automatizaciones", label: "Automatizaciones", icon: Zap },
];

function ReportDetail({ pid, rid, onClose }) {
  const users = useUsers();
  const { data } = useQuery({
    queryKey: ["ops-report", pid, rid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/reporting/${rid}`)).data,
  });
  if (!data) return null;
  const rep = data.report;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="report-detail-dialog">
        <DialogHeader><DialogTitle>Informe {rep.period} — <Chip map={REPORT_STATUS} value={rep.status} /></DialogTitle></DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
          {rep.ai_narrative && (
            <div className="rounded-md border border-violet-200 bg-violet-50/60 p-3" data-testid="report-ai-narrative">
              <p className="mb-1 text-xs font-medium text-violet-700">Borrador IA — {rep.ai_narrative.disclaimer}</p>
              <pre className="whitespace-pre-wrap text-xs text-slate-700">{rep.ai_narrative.text}</pre>
            </div>
          )}
          <div>
            <p className="mb-2 text-sm font-semibold text-slate-800">Afirmaciones con fuente ({(rep.statements || []).length})</p>
            <div className="space-y-2">
              {(rep.statements || []).map((s) => (
                <div key={s.id} className="rounded-md border border-slate-200 p-3" data-testid={`statement-${s.id}`}>
                  <div className="flex items-center gap-2">
                    <Chip map={STATEMENT_NATURE} value={s.nature} testid={`statement-nature-${s.id}`} />
                    <span className="text-xs uppercase text-slate-400">{s.section}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-700">{s.text}</p>
                  {s.source && (
                    <a href={s.source.link} className="mt-1 block text-xs text-blue-700 hover:underline" data-testid={`statement-source-${s.id}`}>
                      Fuente: {s.source.entity_type}/{s.source.entity_id} · datos de {fmtDate(s.source.data_date)} · resp. {userName(users.map, s.source.responsible_id)}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
          {rep.data_quality && (
            <div>
              <p className="mb-2 text-sm font-semibold text-slate-800">Controles de calidad del dato</p>
              <div className="space-y-1">
                {rep.data_quality.map((c) => (
                  <div key={c.check} className="flex items-center justify-between rounded border border-slate-100 px-3 py-1.5 text-xs" data-testid={`report-dq-${c.check}`}>
                    <span>{c.check}{c.critical ? " (crítico)" : ""} — {c.detail}</span>
                    <ToneChip label={c.ok ? "OK" : "Falla"} tone={c.ok ? "success" : "danger"} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.snapshot && (
            <p className="text-xs text-emerald-700" data-testid="report-snapshot-info">
              Snapshot inmutable {data.snapshot.id} emitido el {fmtDateTime(data.snapshot.emitted_at)}.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PeriodosTab({ pid, users }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState("");
  const [detail, setDetail] = useState(null);
  const { data } = useQuery({
    queryKey: ["ops-reporting", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/reporting`)).data,
  });
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/reporting`, { period })).data,
    onSuccess: () => { toast.success("Periodo abierto — tareas de recopilación generadas automáticamente"); setOpen(false); setPeriod(""); qc.invalidateQueries({ queryKey: ["ops-reporting", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const items = data?.periods || [];
  return (
    <div className="space-y-3" data-testid="periodos-tab">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">Abrir periodo → solicitar actualizaciones → borrador IA sobre datos reales → revisión → aprobación → emisión (snapshot inmutable) → cierre.</p>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="create-report-open">Abrir periodo</Button>
      </div>
      {!items.length ? <EmptyState title="Sin periodos de reporting" description="Abre el primer periodo mensual." /> : (
        <Table data-testid="reporting-table">
          <TableHeader><TableRow>
            <TableHead>Periodo</TableHead><TableHead>Estado</TableHead><TableHead>Afirmaciones</TableHead>
            <TableHead>Snapshot</TableHead><TableHead>Abierto por</TableHead><TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                <TableCell>
                  <button className="font-medium text-blue-800 hover:underline" onClick={() => setDetail(r.id)} data-testid={`report-open-${r.id}`}>{r.period}</button>
                </TableCell>
                <TableCell><Chip map={REPORT_STATUS} value={r.status} testid={`report-status-${r.id}`} /></TableCell>
                <TableCell className="text-xs">{(r.statements || []).length || "—"}</TableCell>
                <TableCell className="text-xs">{r.snapshot_id ? <ToneChip label="Archivado" tone="success" testid={`report-snap-${r.id}`} /> : "—"}</TableCell>
                <TableCell className="text-xs">{userName(users.map, r.opened_by)}</TableCell>
                <TableCell><ActionButtons item={r} endpoint={`/proyectos/${pid}/ops/reporting`} invalidate={[["ops-reporting", pid], ["ops-report", pid, r.id]]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {detail && <ReportDetail pid={pid} rid={detail} onClose={() => setDetail(null)} />}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-report-dialog">
          <DialogHeader><DialogTitle>Abrir periodo de reporting</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Periodo (YYYY-MM) *</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="create-report-period" /></div>
            <Button data-testid="create-report-submit" disabled={!period || mut.isPending} onClick={() => mut.mutate()}>Abrir y generar tareas</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CalidadDatoTab({ pid }) {
  const users = useUsers();
  const { data: checks } = useQuery({
    queryKey: ["ops-dq", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/data-quality`)).data,
  });
  if (!checks) return null;
  return (
    <div className="space-y-2" data-testid="dq-tab">
      <p className="text-xs text-slate-500">Un KPI desactualizado muestra la fecha de su fuente, su responsable y una advertencia: nunca se presenta como información actual.</p>
      {checks.map((c) => (
        <div key={c.check} className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-4 py-2.5 ${c.ok ? "border-slate-200" : "border-amber-300 bg-amber-50/60"}`} data-testid={`dq-${c.check}`}>
          <div>
            <p className="text-sm font-medium text-slate-800">{c.label}</p>
            <p className="text-xs text-slate-500">
              Fuente: {c.source_date ? fmtDateTime(c.source_date) : "sin datos"} · Responsable: {userName(users.map, c.responsible_id)}
              {c.age_days != null && ` · hace ${c.age_days} días`}
            </p>
            {!c.ok && <p className="mt-0.5 text-xs font-medium text-amber-700" data-testid={`dq-warning-${c.check}`}>{c.warning}</p>}
          </div>
          <ToneChip label={c.ok ? "Actualizado" : "Desactualizado"} tone={c.ok ? "success" : "warning"} testid={`dq-status-${c.check}`} />
        </div>
      ))}
    </div>
  );
}

function AutomatizacionesTab({ pid }) {
  const { data } = useQuery({
    queryKey: ["ops-automations", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/automatizaciones`)).data,
  });
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="automatizaciones-tab">
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-800">Reglas activas (evento → consecuencias operativas)</p>
        <div className="grid gap-2 md:grid-cols-2">
          {Object.entries(data.rules).map(([k, v]) => (
            <div key={k} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`rule-${k}`}>
              <p className="font-mono text-xs text-slate-500">{k}</p>
              <p className="text-sm text-slate-700">{v}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-800">Eventos ejecutados ({data.events.length})</p>
        {!data.events.length ? <EmptyState title="Sin eventos automatizados" /> : (
          <div className="space-y-2">
            {data.events.map((e) => (
              <div key={e.id} className="rounded-md border border-slate-200 px-4 py-2.5" data-testid={`event-${e.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-800">{e.rule}</p>
                  <span className="text-xs text-slate-400">{fmtDateTime(e.created_at)}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(e.consequences || []).map((c, i) => (
                    <ToneChip key={i} label={`${c.type}${c.id ? `: ${c.id}` : ""}`} tone="info" testid={`event-${e.id}-cons-${i}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Reporting() {
  const [sp, setSp] = useSearchParams();
  const [pid, picker] = useProjectPicker();
  const tab = sp.get("tab") || "periodos";
  const users = useUsers();
  return (
    <div className="space-y-4" data-testid="reporting-page">
      <PageHeader title="Reporting periódico y calidad del dato"
        subtitle="Informe mensual sobre datos reales de los Hitos 1–3. Cada afirmación material abre su fuente; no se emite con datos críticos incompletos."
        actions={picker} testid="reporting-title" />
      <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-px">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setSp((p) => { p.set("tab", t.id); return p; })}
            className={`flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-white text-blue-800 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-800"}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>
      {tab === "periodos" && <PeriodosTab pid={pid} users={users} />}
      {tab === "calidad_dato" && <CalidadDatoTab pid={pid} />}
      {tab === "automatizaciones" && <AutomatizacionesTab pid={pid} />}
    </div>
  );
}
